/**
 * Status Variant Configuration
 * Single source of truth for status → color mappings
 */

import { PillVariant } from '@/components/common/Pill';
import { MatterStatus } from '@/services/types/client-intake-types';
import { IntakeStatus, IntakeStatusValue } from '@/services/types/intake-types';

/**
 * Type-safe mapping of all backend statuses to pill variants.
 * TypeScript will error if any status is missing from this mapping.
 */
type StatusVariantMap = {
  [K in IntakeStatusValue]: PillVariant;
} & {
  [K in MatterStatus]: PillVariant;
};

/**
 * Configuration mapping each status to its visual variant.
 * This is the single source of truth for status colors across Intakes and Matters.
 */
export const STATUS_VARIANT_CONFIG: StatusVariantMap = {
  // Intake Statuses
  [IntakeStatus.CHASE]: 'yellow',
  [IntakeStatus.SIGNED]: 'green',
  [IntakeStatus.ESCALATED]: 'orange',
  [IntakeStatus.IN_PROGRESS]: 'purple',
  [IntakeStatus.SENT_FOR_SIGNATURE]: 'orange',
  [IntakeStatus.ARCHIVED]: 'gray',
  [IntakeStatus.REJECTED]: 'red',
  [IntakeStatus.LOST]: 'gray',

  // Matter Statuses
  [MatterStatus.INTAKE]: 'gray',
  [MatterStatus.CASE_INGESTION]: 'purple',
  [MatterStatus.INITIAL_REVIEW]: 'purple',
  [MatterStatus.CASE_SETUP]: 'purple',
  [MatterStatus.TREATMENT]: 'orange',
  [MatterStatus.TREATMENT_COMPLETE]: 'green',
  [MatterStatus.READY_FOR_DEMAND]: 'orange',
  [MatterStatus.DEMAND_PREPARATION]: 'orange',
  [MatterStatus.DEMAND_LETTER]: 'orange',
  [MatterStatus.LITIGATION]: 'purple',
  [MatterStatus.COMPLETE]: 'green',
  [MatterStatus.HIDDEN]: 'gray',
  [MatterStatus.CLIENT_DROPPED]: 'gray',
  [MatterStatus.TO_BE_DROPPED]: 'orange',
  [MatterStatus.RETURNED_TO_FIRM]: 'yellow',
  [MatterStatus.INGESTED_IN_ERROR]: 'red',
};

export const DEFAULT_STATUS_VARIANT: PillVariant = 'gray';

/**
 * Lead status colors — used by Leads page for both Lexamica and CaseXChange.
 * Separate from the typed STATUS_VARIANT_CONFIG since lead statuses are
 * their own domain, not IntakeStatus or MatterStatus.
 */
const LEAD_STATUS_VARIANTS: Record<string, PillVariant> = {
  new: 'purple',
  reviewing: 'orange',
  intake_created: 'yellow',
  converted: 'green',
  rejected: 'red',
  closed: 'gray',
  error: 'red',
};

/**
 * Get pill variant for a status string with normalization and fallback.
 * Handles different status formats (snake_case, Title Case, etc.)
 *
 * @param status - Status string in any format
 * @returns Pill variant for the status, or gray if unknown
 */
export const getStatusVariant = (status: string): PillVariant => {
  if (!status) return DEFAULT_STATUS_VARIANT;

  // Try direct lookup first (exact match)
  if (status in STATUS_VARIANT_CONFIG) {
    return STATUS_VARIANT_CONFIG[status as keyof typeof STATUS_VARIANT_CONFIG];
  }

  // Lead status lookup
  if (status in LEAD_STATUS_VARIANTS) {
    return LEAD_STATUS_VARIANTS[status];
  }

  // Normalize: lowercase + underscores (e.g., "Initial Review" → "initial_review")
  const normalized = status.trim().toLowerCase().replace(/\s+/g, '_');

  // Try normalized lookup
  if (normalized in STATUS_VARIANT_CONFIG) {
    return STATUS_VARIANT_CONFIG[
      normalized as keyof typeof STATUS_VARIANT_CONFIG
    ];
  }

  // Lead status normalized lookup
  if (normalized in LEAD_STATUS_VARIANTS) {
    return LEAD_STATUS_VARIANTS[normalized];
  }

  // Fuzzy match: find by normalized comparison
  const match = Object.keys(STATUS_VARIANT_CONFIG).find((key) => {
    const normalizedKey = key.toLowerCase().replace(/\s+/g, '_');
    return normalizedKey === normalized;
  });

  if (match) {
    return STATUS_VARIANT_CONFIG[match as keyof typeof STATUS_VARIANT_CONFIG];
  }

  return DEFAULT_STATUS_VARIANT;
};
